import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { formatDate, toLocalDate } from "../utils/format";
import CustomSelect from "../components/CustomSelect";

const DURATIONS = [
  { key: "1m", label: "1 month", months: 1 },
  { key: "3m", label: "3 months", months: 3 },
  { key: "6m", label: "6 months", months: 6 },
  { key: "1y", label: "1 year", months: 12 },
  { key: "2y", label: "2 years", months: 24 },
];

/** YYYY-MM-DD of a local Date. */
function iso(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function subtractMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
}

export default function Reports() {
  const { properties } = useData();
  const navigate = useNavigate();

  const [propertyId, setPropertyId] = useState("all");
  const [durationKey, setDurationKey] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const propertyOptions = useMemo(
    () => [
      { value: "all", label: "All properties" },
      ...properties.map((p) => ({ value: p.id, label: p.name || "Unnamed" })),
    ],
    [properties]
  );

  const applyDuration = (key) => {
    const d = DURATIONS.find((x) => x.key === key);
    if (!d) return;
    const today = new Date();
    setEndDate(toLocalDate(today));
    setStartDate(toLocalDate(subtractMonths(today, d.months)));
    setDurationKey(key);
  };

  const onStartDate = (v) => {
    setStartDate(v);
    setDurationKey(null);
  };
  const onEndDate = (v) => {
    setEndDate(v);
    setDurationKey(null);
  };

  const canView = !!startDate && !!endDate && startDate <= endDate;

  const handleView = () => {
    if (!canView) return;
    const qs = new URLSearchParams({
      startDate,
      endDate,
      propertyId,
    }).toString();
    navigate(`/app/reports/statement?${qs}`);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
          View Statement
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          View and export tenant payment statements for any period.
        </p>
      </div>

      <div className="card p-6 space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              View your statement
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Filter by property and a duration or a custom date range.
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Property
            </label>
            <CustomSelect
              value={propertyId}
              onChange={setPropertyId}
              options={propertyOptions}
              className="min-w-[200px]"
            />
          </div>
        </div>

        <div>
          <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Select duration
          </div>
          <div className="flex flex-wrap gap-2">
            {DURATIONS.map((d) => {
              const active = durationKey === d.key;
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => applyDuration(d.key)}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                    active
                      ? "border-indigo-600 bg-indigo-600 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:border-indigo-400 hover:text-indigo-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-indigo-400 dark:hover:text-indigo-300"
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
            OR
          </span>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>

        <div>
          <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Pick the date range
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <DatePickerField
              label="From"
              value={startDate}
              onChange={onStartDate}
              placeholder="Select start date"
              max={endDate || undefined}
            />
            <DatePickerField
              label="To"
              value={endDate}
              onChange={onEndDate}
              placeholder="Select end date"
              min={startDate || undefined}
            />
          </div>
        </div>

        <div>
          <button
            type="button"
            disabled={!canView}
            onClick={handleView}
            className={`rounded-lg px-6 py-2 text-sm font-semibold transition-colors ${
              canView
                ? "bg-indigo-600 text-white hover:bg-indigo-700"
                : "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
            }`}
          >
            View
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   Custom date picker popover
   ────────────────────────────────────────────────────────── */

function DatePickerField({ label, value, onChange, placeholder, min, max }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:border-gray-500"
      >
        <span className={value ? "" : "text-gray-400 dark:text-gray-500"}>
          {value ? formatDate(value) : placeholder}
        </span>
        <Calendar className="h-4 w-4 text-gray-500" />
      </button>
      {open && (
        <div className="absolute left-0 z-50 mt-2 w-[320px] rounded-xl border border-gray-200 bg-white p-3 shadow-xl dark:border-gray-700 dark:bg-gray-900">
          <MonthGrid
            value={value}
            min={min}
            max={max}
            onPick={(picked) => {
              onChange(picked);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function MonthGrid({ value, min, max, onPick }) {
  const initial = value ? new Date(value + "T00:00:00") : new Date();
  const [view, setView] = useState(
    new Date(initial.getFullYear(), initial.getMonth(), 1)
  );

  const year = view.getFullYear();
  const month = view.getMonth();

  const monthStart = new Date(year, month, 1);
  // ISO week: Monday first column. 0 if Monday, 6 if Sunday.
  const leading = (monthStart.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < leading; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = new Intl.DateTimeFormat("en-GB", {
    month: "long",
    year: "numeric",
  }).format(view);

  const isDisabled = (day) => {
    if (!day) return true;
    const d = iso(new Date(year, month, day));
    if (min && d < min) return true;
    if (max && d > max) return true;
    return false;
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setView(new Date(year, month - 1, 1))}
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="text-sm font-semibold text-gray-900 dark:text-white">
          {monthLabel}
        </div>
        <button
          type="button"
          onClick={() => setView(new Date(year, month + 1, 1))}
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day == null) return <div key={idx} />;
          const dayIso = iso(new Date(year, month, day));
          const selected = value === dayIso;
          const disabled = isDisabled(day);
          return (
            <button
              key={idx}
              type="button"
              disabled={disabled}
              onClick={() => onPick(dayIso)}
              className={`h-9 rounded-md text-sm transition-colors ${
                selected
                  ? "bg-indigo-600 font-semibold text-white"
                  : disabled
                  ? "cursor-not-allowed text-gray-300 dark:text-gray-600"
                  : "text-gray-800 hover:bg-indigo-50 hover:text-indigo-700 dark:text-gray-200 dark:hover:bg-indigo-950 dark:hover:text-indigo-300"
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
